import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, addDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { LabelAttribute } from '../types/labelAttribute';

export function useLabelAttributes() {
  const [attributes, setAttributes] = useState<LabelAttribute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAttributes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const snapshot = await getDocs(collection(db, 'labelAttributes'));
      const attributesData = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      })) as LabelAttribute[];

      setAttributes(attributesData);
    } catch (err) {
      console.error('Error loading label attributes:', err);
      setError(`Failed to load label attributes: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const snapshot = await getDocs(collection(db, 'labelAttributes'));
        if (!mounted) return;

        const attributesData = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        })) as LabelAttribute[];

        setAttributes(attributesData);
      } catch (err) {
        console.error('Error loading label attributes:', err);
        if (mounted) {
          setError(`Failed to load label attributes: ${err instanceof Error ? err.message : String(err)}`);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const getAttributeById = (id: string) => {
    return attributes.find((attr) => attr.id === id);
  };

  const getAttributeByName = (name: string) => {
    return attributes.find((attr) => attr.name === name);
  };

  const createAttribute = async (
    attributeData: Omit<LabelAttribute, 'id' | 'createdAt' | 'updatedAt'>
  ) => {
    try {
      const newAttribute = {
        ...attributeData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, 'labelAttributes'), newAttribute);

      const created: LabelAttribute = {
        ...attributeData,
        id: docRef.id,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      setAttributes((prev) => [...prev, created]);

      return docRef.id;
    } catch (err) {
      console.error('Error creating label attribute:', err);
      throw err;
    }
  };

  const updateAttribute = async (attributeId: string, updates: Partial<LabelAttribute>) => {
    try {
      const { id: _id, ...updatesWithoutId } = updates;
      await updateDoc(doc(db, 'labelAttributes', attributeId), {
        ...updatesWithoutId,
        updatedAt: serverTimestamp(),
      });

      setAttributes((prev) =>
        prev.map((attr) =>
          attr.id === attributeId
            ? { ...attr, ...updatesWithoutId, updatedAt: Date.now() }
            : attr
        )
      );
    } catch (err) {
      console.error('Error updating label attribute:', err);
      throw err;
    }
  };

  const deleteAttribute = async (attributeId: string) => {
    try {
      await deleteDoc(doc(db, 'labelAttributes', attributeId));
      setAttributes((prev) => prev.filter((attr) => attr.id !== attributeId));
    } catch (err) {
      console.error('Error deleting label attribute:', err);
      throw err;
    }
  };

  return {
    attributes,
    loading,
    error,
    refresh: loadAttributes,
    createAttribute,
    updateAttribute,
    deleteAttribute,
    getAttributeByName,
    getAttributeById,
  };
}
